import type { EnemyDef } from '../core/combat';
import { CRYSTAL_SAGE, JUNK_GOLEM, PRISM_WISP, SHARDLING, THE_MASON } from '../data/enemies';

export type NodeType = 'start' | 'fight' | 'elite' | 'rest' | 'store' | 'boss';

export interface MapNode {
  readonly id: string;
  readonly type: NodeType;
  readonly name: string;
  readonly enemies?: readonly EnemyDef[];
  /** Horizontal position for display (0-100). */
  readonly x: number;
  /** Vertical position (0 = start, 100 = boss). */
  readonly y: number;
  /** Node ids this connects to. */
  readonly connections: readonly string[];
  /** Region identity derived from the primary enemy. e.g. 'stone', 'prism', 'crystal', 'shard'. */
  readonly region?: string;
  /** Brief description shown on hover/focus. */
  readonly hint?: string;
}

/** Derive a node's region from its primary enemy. */
export function regionForNode(node: Pick<MapNode, 'enemies' | 'type'>): string {
  if (!node.enemies || node.enemies.length === 0) return 'neutral';
  const primary = node.enemies[0];
  if (!primary) return 'neutral';
  switch (primary.id) {
    case 'junk_golem': return 'stone';
    case 'prism_wisp': return 'prism';
    case 'crystal_sage': return 'crystal';
    case 'the_mason': return 'stone';
    default: return 'shard';
  }
}

const HINTS: Record<string, string> = {
  shardling: 'Opportunist — drops pieces that exploit your board',
  junk_golem: 'Constructor — builds Stone garbage',
  prism_wisp: 'Rearranger — scrambles your colors',
  crystal_sage: 'Hexer — turns gems to Stone',
  the_mason: 'Doom clock — each turn worse than the last',
};

const ELITE_HINTS: Record<string, string> = {
  shardling: 'Elite Opportunist — drops pieces that exploit your board',
  junk_golem: 'Elite Constructor — builds Stone garbage',
  prism_wisp: 'Elite Rearranger — scrambles your colors',
  crystal_sage: 'Elite Hexer — turns gems to Stone',
};

/** Derive a node's hint text from its enemies and type. */
export function hintForNode(node: Pick<MapNode, 'enemies' | 'type'>): string {
  if (!node.enemies || node.enemies.length === 0) {
    return node.type === 'rest' ? 'Rest / Forge — prepare here' : '';
  }
  const primary = node.enemies[0];
  if (!primary) return '';
  if (node.type === 'elite') return ELITE_HINTS[primary.id] ?? HINTS[primary.id] ?? '';
  return HINTS[primary.id] ?? '';
}

export interface RunMap {
  readonly nodes: readonly MapNode[];
  readonly startNode: string;
}

/** Structural rect, so game code never depends on the renderer. */
export interface Box {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** Elite fights reuse existing enemies at higher HP. */
export function scaleEnemy(def: EnemyDef, multiplier: number): EnemyDef {
  return { ...def, maxHp: Math.round(def.maxHp * multiplier) };
}

/**
 * Prototype branching map. Ten nodes, three branch points, both routes
 * converge on The Mason. The six original fights are rearranged across the
 * branches; Junk Golem appears as the Elite (1.5x HP) and again as a normal
 * fight on the common final stretch.
 *
 *     START
 *      ├── Shardling ──┬── Shardling Pair ──┐
 *      │                └── Elite Golem ──┬──┤
 *      └── Shardling ──┬── Rest ──────────┘  ├── Wisp/Sage ── Junk Golem ── BOSS
 *                      └── Elite Golem ──────┘
 */
export function generateMap(_seed?: number): RunMap {
  const raw: Omit<MapNode, 'region' | 'hint'>[] = [
    { id: 'start', type: 'start', name: 'Start', x: 50, y: 0, connections: ['f-s1', 'f-s2'] },
    { id: 'f-s1', type: 'fight', name: 'Shardling', enemies: [SHARDLING], x: 20, y: 18, connections: ['f-pair', 'e-golem'] },
    { id: 'f-s2', type: 'fight', name: 'Shardling', enemies: [SHARDLING], x: 80, y: 18, connections: ['e-golem', 'rest'] },
    { id: 'f-pair', type: 'fight', name: 'Shardling Pair', enemies: [SHARDLING, SHARDLING], x: 14, y: 40, connections: ['f-wisp'] },
    { id: 'e-golem', type: 'elite', name: 'Elite Golem', enemies: [scaleEnemy(JUNK_GOLEM, 1.5)], x: 50, y: 40, connections: ['f-wisp', 'f-sage'] },
    { id: 'rest', type: 'rest', name: 'Rest / Forge', x: 86, y: 40, connections: ['f-sage'] },
    { id: 'f-wisp', type: 'fight', name: 'Prism Wisp', enemies: [PRISM_WISP, SHARDLING], x: 26, y: 62, connections: ['f-golem'] },
    { id: 'f-sage', type: 'fight', name: 'Crystal Sage', enemies: [CRYSTAL_SAGE], x: 74, y: 62, connections: ['f-golem'] },
    { id: 'f-golem', type: 'fight', name: 'Junk Golem', enemies: [JUNK_GOLEM], x: 50, y: 80, connections: ['boss'] },
    { id: 'boss', type: 'boss', name: 'The Mason', enemies: [THE_MASON], x: 50, y: 100, connections: [] },
  ];
  return {
    startNode: 'start',
    nodes: raw.map(n => ({
      ...n,
      region: regionForNode(n),
      hint: hintForNode(n),
    })),
  };
}

export function nodeById(map: RunMap, id: string): MapNode | null {
  return map.nodes.find(n => n.id === id) ?? null;
}

/** Nodes directly reachable from `id`. */
export function nodesFrom(map: RunMap, id: string): MapNode[] {
  const node = nodeById(map, id);
  if (!node) return [];
  const out: MapNode[] = [];
  for (const cid of node.connections) {
    const child = nodeById(map, cid);
    if (child) out.push(child);
  }
  return out;
}

export function bossNode(map: RunMap): MapNode | null {
  return map.nodes.find(n => n.type === 'boss') ?? null;
}

/** Every node reachable from `id` by following connections forward. */
export function reachableFrom(map: RunMap, id: string): Set<string> {
  const seen = new Set<string>();
  const queue = [id];
  while (queue.length > 0) {
    const cur = queue.shift();
    if (cur === undefined || seen.has(cur)) continue;
    seen.add(cur);
    const node = nodeById(map, cur);
    if (!node) continue;
    for (const cid of node.connections) queue.push(cid);
  }
  return seen;
}

/**
 * Nodes within `depth` forward hops of `id`, plus everything already
 * visited — the player's fog of war for the prototype map screen.
 */
export function visibleNodes(map: RunMap, id: string, depth: number): Set<string> {
  const seen = new Set<string>();
  let frontier = [id];
  for (let d = 0; d <= depth; d++) {
    const next: string[] = [];
    for (const cur of frontier) {
      if (seen.has(cur)) continue;
      seen.add(cur);
      const node = nodeById(map, cur);
      if (!node) continue;
      for (const cid of node.connections) next.push(cid);
    }
    frontier = next;
  }
  return seen;
}

// --- screen geometry -------------------------------------------------------

const NODE_PAD = 30;
export const NODE_HIT_R = 26;

/** Screen-space centre of a node inside a graph area. y=0 sits at the bottom. */
export function nodeCenter(node: MapNode, area: Box): { x: number; y: number } {
  const iw = Math.max(0, area.w - NODE_PAD * 2);
  const ih = Math.max(0, area.h - NODE_PAD * 2);
  return {
    x: area.x + NODE_PAD + (node.x / 100) * iw,
    y: area.y + area.h - NODE_PAD - (node.y / 100) * ih,
  };
}

/** Clickable square around a node. Shared by the renderer and the hit-tester. */
export function nodeHitRect(node: MapNode, area: Box): Box {
  const c = nodeCenter(node, area);
  return { x: c.x - NODE_HIT_R, y: c.y - NODE_HIT_R, w: NODE_HIT_R * 2, h: NODE_HIT_R * 2 };
}
